import React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

import { colors } from '../../theme/tokens';

/**
 * Design-system calendar date picker — the single replacement for native
 * <input type="date"> / type="datetime-local"> across the app.
 *
 * PRESENTATION ONLY. It is a thin wrapper over MUI X (already a dependency and
 * already used in OnboardingTab) themed with the app's design tokens. The
 * onChange contract is byte-identical to the native input it replaces:
 *   - date      → emits "YYYY-MM-DD"
 *   - date+time → emits "YYYY-MM-DDTHH:mm"  (same string as <input type=datetime-local>)
 * All behavior (min/max, disabled, required, error, format) flows through props
 * — nothing hardcoded at call sites.
 */

const DATE_FMT = 'YYYY-MM-DD';
const DATETIME_FMT = 'YYYY-MM-DDTHH:mm';

export interface AppDatePickerProps {
  /** Current field string (unchanged format). Empty string = no value. */
  value: string;
  /** Receives the SAME string format the native input emitted. */
  onChange: (value: string) => void;
  /** DATE+TIME variant — use ONLY where the field already had a time component. */
  withTime?: boolean;
  /**
   * Show the time as 12-hour with an AM/PM selector (display only). The value
   * emitted by onChange is ALWAYS the 24-hour `YYYY-MM-DDTHH:mm` string, so the
   * payload and the DB column are unaffected by this flag.
   */
  ampm?: boolean;
  /** Min/max constraints as the field's string format (maps to MUI minDate/maxDate). */
  min?: string;
  max?: string;
  /** Display format in the text field. Defaults: MM/DD/YYYY (+ ' HH:mm' with time). */
  format?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  label?: string;
  placeholder?: string;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  /** Disable individual days (parity with fields that disable specific dates). */
  shouldDisableDate?: (day: Dayjs) => boolean;
  /** Passthrough sx merged onto the text field root. */
  sx?: Record<string, unknown>;
}

// Selected day = brand amber on black text (matches the app's active states);
// today = amber ring. Token-driven, scoped to this component's popover.
const daySx = {
  '&.MuiPickersDay-root': { fontFamily: 'Manrope, sans-serif', fontSize: 13 },
  '&.Mui-selected': {
    backgroundColor: colors.brand,
    color: colors.textPrimary,
    '&:hover, &:focus': { backgroundColor: colors.brandHover },
  },
  '&.MuiPickersDay-today': { borderColor: colors.brand },
} as const;

export default function AppDatePicker({
  value,
  onChange,
  withTime = false,
  ampm = false,
  min,
  max,
  format,
  disabled,
  readOnly,
  required,
  error,
  helperText,
  label,
  placeholder,
  fullWidth = true,
  size = 'small',
  shouldDisableDate,
  sx,
}: AppDatePickerProps) {
  // Storage format is INDEPENDENT of the display format: always 24-hour
  // `YYYY-MM-DDTHH:mm` so the payload (and the DB timestamp column) never carries
  // an AM/PM rendering. `ampm` only changes what the operator reads.
  const storeFmt = withTime ? DATETIME_FMT : DATE_FMT;
  const displayFmt = format ?? (withTime ? (ampm ? 'MM/DD/YYYY hh:mm A' : 'MM/DD/YYYY HH:mm') : 'MM/DD/YYYY');
  const parsed = value ? dayjs(value) : null;

  const handleChange = (d: Dayjs | null) => {
    onChange(d && d.isValid() ? d.format(storeFmt) : '');
  };

  const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 14 }, ...(sx ?? {}) };

  const slotProps = {
    textField: {
      size,
      fullWidth,
      required,
      error,
      helperText,
      placeholder,
      sx: inputSx,
      InputLabelProps: { shrink: true },
    },
    day: { sx: daySx },
    actionBar: { actions: ['today', 'clear'] as ('today' | 'clear')[] },
    desktopPaper: { sx: { borderRadius: '12px', border: `1px solid ${colors.borderLight}` } },
  };

  const minDate = min ? dayjs(min) : undefined;
  const maxDate = max ? dayjs(max) : undefined;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {withTime ? (
        <DateTimePicker
          value={parsed}
          onChange={handleChange}
          format={displayFmt}
          ampm={ampm}
          disabled={disabled}
          readOnly={readOnly}
          label={label}
          minDate={minDate}
          maxDate={maxDate}
          shouldDisableDate={shouldDisableDate}
          slotProps={slotProps}
        />
      ) : (
        <DatePicker
          value={parsed}
          onChange={handleChange}
          format={displayFmt}
          disabled={disabled}
          readOnly={readOnly}
          label={label}
          minDate={minDate}
          maxDate={maxDate}
          shouldDisableDate={shouldDisableDate}
          slotProps={slotProps}
        />
      )}
    </LocalizationProvider>
  );
}
